import clsx from 'clsx';
import { omit } from 'lodash';
import { useMemo, ReactElement, useId } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LoadingGradient } from '@provider-directory/components/loading-gradient/loading-gradient.component';
import { useCachedSlots } from '@provider-directory/contexts/CachedSlotsContext';
import { useProvider } from '@provider-directory/contexts/ProviderContext';
import { sortLocationOptionsForDropdown } from '@provider-directory/functions/sort-locations';
import LocationSelector, {
  LocationSelectorProps,
  createLocationOption,
} from '../../location-selector/location-selector.component';
import { AppointmentTimeFormFieldValues } from '../select-appointment-form-values';

export const ControlledLocationSelect = ({
  isLoading,
  isDisabled,
}: {
  isLoading?: boolean;
  isDisabled?: boolean;
}): ReactElement => {
  const departmentLabelId = useId();
  const { t } = useTranslation();
  const { control, watch } = useFormContext<AppointmentTimeFormFieldValues>();
  const { provider } = useProvider();
  const { field } = useController<
    AppointmentTimeFormFieldValues,
    'departmentId'
  >({ name: 'departmentId', control });
  const reasonForVisit = watch('reasonForVisit');
  const { nearestAvailability } = useCachedSlots();

  const locations = useMemo<LocationSelectorProps['locations']>(
    () =>
      sortLocationOptionsForDropdown(
        (provider?.locations ?? [])
          .filter(({ ehrExternalId }) => !!ehrExternalId)
          .map((lt) => createLocationOption(lt, nearestAvailability)),
        field.value,
      ),
    [provider?.locations, nearestAvailability, field.value],
  );
  return (
    <div
      className={clsx('flex flex-col gap-y-3', {
        'sr-only': reasonForVisit === 'video',
      })}
    >
      {!isLoading ? (
        <div className={'flex justify-between'}>
          <span
            className={'text-body-medium-semibold text-gray-950'}
            id={departmentLabelId}
          >
            {t('appointmentSlot.departmentId.label')}
          </span>
          {locations.length > 1 && !isLoading && (
            <span className={'text-body-xsmall text-gray-700'}>
              {t('appointmentSlot.departmentId.helpText', {
                count: locations.length - 1,
              })}
            </span>
          )}
        </div>
      ) : (
        <LoadingGradient className={'h-[26px] w-[67px] rounded-sm'} />
      )}
      <LocationSelector
        {...omit(field, 'ref')}
        mobileDrawerTitle={t('appointmentSlot.departmentId.label')}
        ariaLabelledBy={departmentLabelId}
        locations={locations}
        location={field.value}
        isLoading={isLoading}
        isDisabled={isDisabled}
      />
    </div>
  );
};
